import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { MarkdownComponent } from 'ngx-markdown';
import { FormsModule } from '@angular/forms';

//* ChatMessage type to be used in both the Component and API calls
export interface ChatMessage {
  content: string;
  role: 'user' | 'assistant';
}

//* Global declaration for TypeScript to allow the use of window.MathJax
declare global {
  interface Window {
    MathJax: {
      typeset: () => void;
      typesetPromise: () => Promise<void>;
      startup: { promise: Promise<void> };
      tex?: any;
      svg?: any;
    };
  }
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css'],
  imports: [NgClass, MarkdownComponent, FormsModule],
})
export class ChatbotComponent
  implements AfterViewChecked, OnChanges, OnDestroy, OnInit
{
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @Input() avatarPath = '';
  @Input() initialMessage = 'Enviar';
  @Input() disabledMessage = 'No puedes enviar mensajes';
  @Input() assistantResponse = '';
  @Input() disableUserInput!: boolean;
  @Output() messageHistory = new EventEmitter<ChatMessage[]>();
  messages: ChatMessage[] = [];
  userMessage = '';
  // If we are loading an old conversation instead of a new one this variable hold
  // the messages to load into the system. After that its no used anymore
  @Input() messagesToLoad: ChatMessage[] | null = null;

  editingIndex: number | null = null;
  editMessageContent = '';
  isEditMode = false;

  ngOnInit(): void {
    // If messagesToLoad is not null then we are loading an old Chat Session,
    // otherwise is a new one
    if (this.messagesToLoad != null) {
      this.messages = this.messagesToLoad;
      setTimeout(() => this.scrollToBottom(), 0);
    } else {
      this.messageHistory.emit(this.messages);
      this.messages.push({ content: '...', role: 'assistant' });
    }
  }

  ngAfterViewChecked() {
    if (window.MathJax) {
      window.MathJax.typeset();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assistantResponse']) {
      if (this.assistantResponse !== '')
        this.messages[this.messages.length - 1] = {
          content: this.assistantResponse,
          role: 'assistant',
        };
    }
  }

  ngOnDestroy(): void {
    this.clear();
  }

  adjustTextareaHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }

  startEdit(index: number): void {
    if (this.disableUserInput) return;
    this.isEditMode = true;
    this.editingIndex = index;
    this.editMessageContent = this.messages[index].content;
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.editingIndex = null;
    this.editMessageContent = '';
  }

  saveEdit(index: number): void {
    const newContent = this.editMessageContent.trim();
    if (newContent && newContent !== this.messages[index].content) {
      this.messages[index].content = newContent;
      this.messages = this.messages.slice(0, index + 1);
      this.messages.push({ content: '...', role: 'assistant' });
      this.messageHistory.emit(
        this.messages.filter((m) => m.content !== '...'),
      );
    }
    this.isEditMode = false;
    this.editingIndex = null;
  }

  sendMessage(): void {
    const content = this.userMessage.trim();
    if (content) {
      this.messages.push({ content, role: 'user' });
      this.userMessage = '';
      const textarea = document.querySelector('textarea');
      if (textarea) textarea.style.height = 'auto';
      setTimeout(() => this.scrollToBottom(), 0);
      this.messageHistory.emit(this.messages);
      setTimeout(() => {}, 500);
      this.messages.push({ content: '...', role: 'assistant' });
    }
  }

  clear(): void {
    this.messages = [];
    this.userMessage = '';
  }

  scrollToBottom(): void {
    this.messagesContainer.nativeElement.scrollTop =
      this.messagesContainer.nativeElement.scrollHeight;
  }
}
